import { JSX, useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { ClientBenchmarkingModel } from "@app/client";
import { ClientContext } from "@app/admin/contexts/ClientContext";
import { BasicState, GeographyService } from "@app/geography";
import { NetworkTaxonomyLineOfBusinessSimple } from "@app/network-taxonomies/networkTaxonomy";
import NetworkTaxonomyService from "@app/network-taxonomies/networkTaxonomyService";
import { LoadingSpinner } from "@app/shared";
import { QESMultiSelect, QESSelect } from "@app/shared/qes-standards";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { MultiselectItem, MultiselectOnChangeResponse } from "@app/shared/qes-standards/forms/Multiselect";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { BenchmarkingQHPService } from "@app/overviews/network-benchmarking-dashboard";
import { FeatureFlagContext, Features } from "@app/feature-flags";

interface ClientBenchmarkingProps {
  disabled?: boolean;
}

const ClientBenchmarking = (props: ClientBenchmarkingProps): JSX.Element => {
  const [availableStates, setAvailableStates] = useState<BasicState[]>([]);
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<NetworkTaxonomyLineOfBusinessSimple[]>([]);
  const [linesOfBusinessLoading, setLinesOfBusinessLoading] = useState<boolean>(true);
  const [selectAllChecked, setSelectAllChecked] = useState<boolean>(false);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<number>(null);
  const [stateOptions, setStateOptions] = useState<{ [key: string]: MultiselectItem }>(null);
  const [statesLoading, setStatesLoading] = useState<boolean>(true);

  const [availableQhpStates, setAvailableQhpStates] = useState<BasicState[]>([]);
  const [qhpStateOptions, setQhpStateOptions] = useState<{ [key: string]: MultiselectItem }>(null);
  const [qhpStatesLoading, setQhpStatesLoading] = useState<boolean>(true);
  const clientContext = useContext(ClientContext);

  const geographyService = new GeographyService();
  const networkTaxonomyService = new NetworkTaxonomyService();
  const benchmarkingQHPService = new BenchmarkingQHPService();
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;

  useEffect(() => {
    getLinesOfBusiness();
    if (featureFlags.has(Features.NETWORK_BENCHMARKING_MARKET_PLACE)) {
      getQhpStates();
    }
    else {
      setAvailableQhpStates(null);
      setQhpStateOptions(null);
      setQhpStatesLoading(null);
    }

    getStates();
  }, []);

  useEffect(() => {
    const newStateOptions = cloneDeep(stateOptions);
    const newQhpStateOptions = cloneDeep(qhpStateOptions);

    if (!!selectedLineOfBusiness) {
      const clientBenchmarking: ClientBenchmarkingModel = clientContext.clientBenchmarking.find(x => x.lineOfBusinessId === selectedLineOfBusiness);
      const selectedStateAbbreviations = clientBenchmarking.stateAbbreviations || [];
      const selectedLineOfBusinessName = getLineOfBusinessName(selectedLineOfBusiness);

      // For "Marketplace" LOB, update QHP state options
      if (selectedLineOfBusinessName === "Marketplace") {
        const newQhpStateNames = selectedStateAbbreviations.map((stateAbbreviation) => getStateName(stateAbbreviation)).filter(Boolean);

        for (const x of Object.keys(newQhpStateOptions)) {
          if (newQhpStateNames.includes(x)) {
            newQhpStateOptions[x].currentState = CheckboxStateEnum.Checked;
          } else {
            newQhpStateOptions[x].currentState = CheckboxStateEnum.Unchecked;
          }
        }

        setQhpStateOptions(newQhpStateOptions);
        if (clientBenchmarking.stateAbbreviations.length === availableQhpStates.length) {
          setSelectAllChecked(true);
        } else {
          setSelectAllChecked(false);
        }
      }

      else {
        // For non-Marketplace LOB, update normal state options
        const newStateNames = selectedStateAbbreviations.map((stateAbbreviation) => getStateName(stateAbbreviation)).filter(Boolean);

        for (const x of Object.keys(newStateOptions)) {
          if (newStateNames.includes(x)) {
            newStateOptions[x].currentState = CheckboxStateEnum.Checked;
          } else {
            newStateOptions[x].currentState = CheckboxStateEnum.Unchecked;
          }
        }

        setStateOptions(newStateOptions);
        if (clientBenchmarking.stateAbbreviations.length === availableStates.length) {
          setSelectAllChecked(true);
        } else {
          setSelectAllChecked(false);
        }
      }
    }

    else {
      // if no lines of business are selected, deselect all states in case any are selected
      // from when there were lines of business selected in order to avoid visual confusion
      if (!!stateOptions) {
        for (const x of Object.keys(newStateOptions)) {
          newStateOptions[x].currentState = CheckboxStateEnum.Unchecked;
        }

        setStateOptions(newStateOptions);
      }

      if (!!qhpStateOptions) {
        for (const x of Object.keys(newQhpStateOptions)) {
          newQhpStateOptions[x].currentState = CheckboxStateEnum.Unchecked;
        }

        setQhpStateOptions(newQhpStateOptions);
      }
    }
  }, [clientContext.clientBenchmarking, selectedLineOfBusiness]);

  const getLineOfBusinessName = (lineOfBusinessId: number): string => {
    const lineOfBusiness = lineOfBusinessOptions.find(x => x.lineOfBusinessId === lineOfBusinessId);
    return lineOfBusiness?.name;
  }

  const getLinesOfBusiness = (): void => {
    networkTaxonomyService.getLinesOfBusiness().then((data) => {
      if (data?.length) {

        // For non-Marketplace LOB filter out Marketplace
        if (!featureFlags.has(Features.NETWORK_BENCHMARKING_MARKET_PLACE)) {
          const filteredData = data.filter(item => item.name !== "Marketplace");
          setLineOfBusinessOptions(filteredData);
        }
        else {
          setLineOfBusinessOptions(data);
        }

        setLinesOfBusinessLoading(false);
      }
    });
  }

  const getStateName = (stateAbbreviation: string): string => {
    const matchedStateModel: BasicState = availableStates.find((x) => x.abbreviation === stateAbbreviation);
    return matchedStateModel?.name;
  }

  const getStates = (): void => {
    geographyService.getStatesForDroplist().then((data) => {
      if (data?.length) {
        const states: { [key: string]: MultiselectItem } = {};
        const unwantedStates = ["GU", "VI"];
        data = data.filter(x => !unwantedStates.includes(x.abbreviation));

        data.forEach((r) => Object.assign(states,
          {
            [r.name]: {
              currentState: CheckboxStateEnum.Unchecked,
              displayValue: r.name,
            }
          }
        ));

        setAvailableStates(data);
        setStateOptions(states);
        setStatesLoading(false);
      }
    });
  }

  const getQhpStates = (): void => {
    benchmarkingQHPService.getQhpStates().then((data) => {
      if (data?.length) {
        const qhpStates: { [key: string]: MultiselectItem } = {};
        const unwantedStates = ["GU", "VI"];
        data = data.filter(x => !unwantedStates.includes(x.abbreviation));

        data.forEach((r) => Object.assign(qhpStates,
          {
            [r.name]: {
              currentState: CheckboxStateEnum.Unchecked,
              displayValue: r.name,
            }
          }
        ));

        setAvailableQhpStates(data);
        setQhpStateOptions(qhpStates);
        setQhpStatesLoading(false);
      }
    });
  }

  const onLineOfBusinessChange = (lineOfBusinessId: number): void => {
    const clientBenchmarking: ClientBenchmarkingModel[] = cloneDeep(clientContext.clientBenchmarking);
    const storedClientBenchmarkingModel: ClientBenchmarkingModel = clientBenchmarking.find(x => x.lineOfBusinessId === lineOfBusinessId);
    if (!storedClientBenchmarkingModel) {
      // if a stored client benchmarking model for the line of business does not exist, create one
      const newClientBenchmarkingModel: ClientBenchmarkingModel = {
        lineOfBusinessId,
        stateAbbreviations: [],
      };

      clientBenchmarking.push(newClientBenchmarkingModel);
    }

    clientContext.setClientBenchmarking(clientBenchmarking);
    setSelectedLineOfBusiness(lineOfBusinessId);
  }

  const onSelectAllToggle = () => {
    const newSelectAllValue = !selectAllChecked;
    const newStateOptions = cloneDeep(stateOptions);
    const newQhpStateOptions = cloneDeep(qhpStateOptions);

    // For "Marketplace" LOB, update QHP state options
    const selectedLineOfBusinessName = getLineOfBusinessName(selectedLineOfBusiness);
    if (selectedLineOfBusinessName === "Marketplace") {
      for (const x of Object.keys(newQhpStateOptions)) {
        if (newSelectAllValue) {
          newQhpStateOptions[x].currentState = CheckboxStateEnum.Checked;
        } else {
          newQhpStateOptions[x].currentState = CheckboxStateEnum.Unchecked;
        }
      }

      setQhpStateOptions(newQhpStateOptions);
      setSelectAllChecked(newSelectAllValue);
      updateClientBenchmarkingModel(newSelectAllValue ? availableQhpStates : []);
    }
    // For non-Marketplace LOB, update normal state options
    else {

      for (const x of Object.keys(newStateOptions)) {
        if (newSelectAllValue) {
          newStateOptions[x].currentState = CheckboxStateEnum.Checked;
        } else {
          newStateOptions[x].currentState = CheckboxStateEnum.Unchecked;
        }
      }

      setStateOptions(newStateOptions);
      setSelectAllChecked(newSelectAllValue);
      updateClientBenchmarkingModel(newSelectAllValue ? availableStates : []);
    }
  }

  const updateClientBenchmarkingModel = (newStates: BasicState[]) => {
    const clientBenchmarkingModels: ClientBenchmarkingModel[] = cloneDeep(clientContext.clientBenchmarking);
    // assign selected states to selected line of business
    const model: ClientBenchmarkingModel = clientBenchmarkingModels.find(x => x.lineOfBusinessId === selectedLineOfBusiness);

    if (!!model) {
      model.stateAbbreviations = cloneDeep(newStates.map(x => x.abbreviation));
    }

    clientContext.setClientBenchmarking(clientBenchmarkingModels);
  }

  const updateStates = (response: MultiselectOnChangeResponse): void => {
    const selectedStates: string[] = response.checkedKeys;

    // Convert selected states to BasicState
    const newStates = selectedStates.map((state) => {
      return availableStates.find(x => x.name === state);
    }).filter(Boolean).sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));

    // Update clientBenchmarking model with new states
    updateClientBenchmarkingModel(newStates);

    // Adjust select all checkbox
    const model: ClientBenchmarkingModel = clientContext.clientBenchmarking.find(x => x.lineOfBusinessId === selectedLineOfBusiness);
    if (model?.stateAbbreviations.length === availableStates.length) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }
  };

  return (
    <div>
      <div className="row">
        <div className="col-md-6">
          <div className="row">
            <div className="col-md-6">
              {(!linesOfBusinessLoading && !clientContext.clientBenchmarkingLoading && !!lineOfBusinessOptions.length) ? (
                <QESSelect
                  disabled={!lineOfBusinessOptions?.length}
                  testId="client-admin__benchmarking--line-of-business"
                  label="Authorized Line Of Business"
                  onChange={(e) => onLineOfBusinessChange(parseInt(e.target.value))}
                  value={selectedLineOfBusiness || ""}
                >
                  {lineOfBusinessOptions.map((lineOfBusiness, index) => (
                    <option
                      key={index}
                      value={lineOfBusiness.lineOfBusinessId}
                    >
                      {lineOfBusiness.name}
                    </option>
                  ))}
                </QESSelect>
              ) : (
                <LoadingSpinner />
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <QESCheckbox
                checked={selectAllChecked}
                containerClassName="mb-0 mt-3"
                disabled={props.disabled || !selectedLineOfBusiness}
                label="Select All States"
                testId="client-admin__benchmarking--select-all"
                onChange={() => onSelectAllToggle()}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              {(!statesLoading && !clientContext.clientBenchmarkingLoading && !!stateOptions &&
                (featureFlags.has(Features.NETWORK_BENCHMARKING_MARKET_PLACE) ? (!qhpStatesLoading && !!qhpStateOptions) : true)) ? (
                <QESMultiSelect
                  containerClassName="mb-3 mt-3"
                  disabled={props.disabled || !selectedLineOfBusiness}
                  label="Authorized States"
                  onChange={updateStates}
                  options={getLineOfBusinessName(selectedLineOfBusiness) === "Marketplace" ? qhpStateOptions : stateOptions}
                  required={true}
                  testId="client-admin__benchmarking--states"
                />
              ) : (
                <LoadingSpinner />
              )}
            </div>
          </div>
          <QESCheckbox containerClassName="col-12 mb-4" label='Competitive Insights Data Source' testId="new-client__network-benchmarking-data-source" disabled={true} checked={clientContext.networkBenchmarkingDataSource} />
        </div>
        <div className="offset-md-2 col-md-4">
          <label className="label-m mb-0">
            Selected States Overview
          </label>
          {
            (!featureFlags.has(Features.NETWORK_BENCHMARKING_MARKET_PLACE)
              ? clientContext.clientBenchmarking.filter(x => x.lineOfBusinessId !== 3)
              : clientContext.clientBenchmarking).map((group) => (
                <div className="mt-3" key={group.lineOfBusinessId}>
                  <label className="label-s mb-0">
                    {getLineOfBusinessName(group.lineOfBusinessId)}
                  </label>
                  <div className="client-admin__benchmarking--state-box">
                    {group.stateAbbreviations?.map((stateAbbreviation) => (
                      <div key={stateAbbreviation}>
                        {getStateName(stateAbbreviation)}
                      </div>
                    ))}
                  </div>
                </div>
              ))
          }
        </div>
      </div>
    </div>
  );
}

export default ClientBenchmarking;
